import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, Link, createRootRouteWithContext, useRouter } from "@tanstack/react-router";

import { Toaster } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ThemeProvider, useTheme } from "@/components/theme-provider";
import { RequireSession, SessionProvider } from "@/components/session-provider";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

// Document head, favicon and the pre-paint theme script live in index.html.
// This is a SPA — there is no server-rendered shell.
export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function ThemedToaster() {
  const { resolvedTheme } = useTheme();
  return <Toaster richColors theme={resolvedTheme} position="top-right" />;
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SessionProvider>
          {/*
            Nothing inside renders until the session is known.

            Previously AppShell and Outlet painted immediately while /api/me was still in
            flight, so an unauthenticated visitor saw the internal dashboard — navigation,
            queues, admin entries, and mock ticket rows that look real — for the second or
            two before the 401 arrived. It read as a successful sign-in followed by an
            error. No real data was reachable, but the structure of an internal system is
            not something to show a stranger while deciding whether they are one.
          */}
          <RequireSession>
            <AppShell>
              <Outlet />
            </AppShell>
            <ThemedToaster />
          </RequireSession>
        </SessionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
